import { useState, FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { signInAnonymously, signOut } from "firebase/auth";
import { httpsCallable, FunctionsError } from "firebase/functions";
import { auth, functions } from "../firebase";
import { EduClassResolution } from "../types";

export default function StudentJoin() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const join = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const cleanCode = code.trim().toUpperCase();
      const cleanName = name.trim();
      if (!cleanName) throw new Error("Enter your first name.");
      if (cleanCode.length !== 6) throw new Error("Class code is 6 letters and numbers.");

      // Anonymous auth first: the resolver requires a signed-in caller, and
      // the same anonymous uid is what scopes this student's Storage upload.
      // A signed-in REAL account (teacher demoing the join flow) must be
      // signed out first, never reused: student writes must ride an
      // anonymous uid (the rules enforce it at create). An existing
      // anonymous session is kept.
      if (auth.currentUser && !auth.currentUser.isAnonymous) {
        await signOut(auth);
      }
      if (!auth.currentUser) await signInAnonymously(auth);

      // Students have no read access to edu_classes (the doc holds the join
      // code for the whole class). The server resolves the code to exactly
      // one sanitized class instead.
      const resolve = httpsCallable<{ code: string }, EduClassResolution>(
        functions,
        "resolveEduClass"
      );
      const resolved = (await resolve({ code: cleanCode })).data;

      sessionStorage.setItem("eduStudentName", cleanName);
      sessionStorage.setItem("eduClassCode", cleanCode);
      sessionStorage.setItem("eduClassId", resolved.classId);
      navigate(`/play/${resolved.classId}`);
    } catch (e) {
      const code = (e as FunctionsError)?.code;
      const msg =
        code === "functions/not-found"
          ? "That code didn't match a class."
          : e instanceof Error
            ? e.message
            : "Failed to join.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="screen">
      <div className="card">
        <h1>PhotoFun EDU</h1>
        <p className="muted">Enter your first name and your class code to begin.</p>
        <form onSubmit={join}>
          <label>
            First name
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="off"
              autoCapitalize="words"
              required
            />
          </label>
          <label>
            Class code
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              maxLength={6}
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              style={{ letterSpacing: "0.3em", textTransform: "uppercase", fontWeight: 700 }}
              required
            />
          </label>
          {error && <p className="error">{error}</p>}
          <button type="submit" disabled={loading}>
            {loading ? "Joining..." : "Join"}
          </button>
        </form>
      </div>
    </main>
  );
}
